import React, { useState, useContext, useEffect } from 'react';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { UserData } from '../../contexts/AuthContext';
import AuthContext from '../../contexts/AuthContext';
import { PencilIcon, CameraIcon, ChatBubbleLeftRightIcon, UserIcon, UsersIcon } from '@heroicons/react/24/outline';
import { uploadToCloudinary } from '../../utils/cloudinary';
import { Link } from 'react-router-dom';


interface ProfileHeaderProps {
  userData: UserData;
  isFollowing?: boolean;
  toggleFollow?: () => Promise<void>;
  isCurrentUser: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ userData, isFollowing = false, toggleFollow, isCurrentUser }) => {
  const { currentUser, updateUserProfile } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(userData?.displayName || '');
  const [bio, setBio] = useState(userData?.bio || '');
  const [location, setLocation] = useState(userData?.location || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  // Actualizar estados cuando cambia userData
  useEffect(() => {
    if (userData) {
      setDisplayName(userData.displayName || '');
      setBio(userData.bio || '');
      setLocation(userData.location || '');
    }
  }, [userData]);

  const handleProfilePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (!file || !currentUser) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El tamaño máximo es 2MB.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten imágenes.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(10); // Iniciar progreso

      // Crear un intervalo para simular progreso mientras se sube a Cloudinary
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      // Subir a Cloudinary
      console.log('Subiendo foto de perfil a Cloudinary...');
      const cloudinaryUrl = await uploadToCloudinary(file);
      console.log('Foto subida exitosamente:', cloudinaryUrl);

      // Limpiar intervalo de progreso
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Usar el método updateUserProfile del AuthContext para actualizar la foto de perfil
      // Esto se encargará de actualizar Firebase Auth, Firestore y sincronizar con posts/comentarios
      await updateUserProfile(userData.displayName, cloudinaryUrl);

      // Recargar página para reflejar cambios
      window.location.reload();
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Error al actualizar la foto de perfil. Inténtalo de nuevo.');
      setIsUploading(false);
    }
  };

  const handleCoverPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (!file || !currentUser) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten imágenes.');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(10); // Iniciar progreso

      // Crear un intervalo para simular progreso mientras se sube a Cloudinary
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      // Subir a Cloudinary
      console.log('Subiendo foto de portada a Cloudinary...');
      const cloudinaryUrl = await uploadToCloudinary(file);
      console.log('Foto de portada subida exitosamente:', cloudinaryUrl);

      // Limpiar intervalo de progreso
      clearInterval(progressInterval);
      setUploadProgress(100);

      // Actualizar documento del usuario con la nueva foto de portada
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        coverPhotoURL: cloudinaryUrl,
        updatedAt: new Date()
      });

      // Recargar página para reflejar cambios
      window.location.reload();
    } catch (error) {
      console.error('Error uploading cover photo:', error);
      alert('Error al actualizar la foto de portada. Inténtalo de nuevo.');
      setIsUploading(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (!currentUser) return;

    try {
      // Usar el método updateUserProfile del AuthContext para actualizar el perfil
      // Esto se encargará de actualizar Firebase Auth, Firestore y sincronizar con posts/comentarios
      await updateUserProfile(displayName, userData.photoURL || '');

      // Actualizar también los campos adicionales que no están en el método updateUserProfile
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        bio,
        location,
        updatedAt: new Date()
      });

      setIsEditing(false);

      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error al actualizar el perfil. Inténtalo de nuevo.');
    }
  };

  const handleToggleFollow = async () => {
    if (!toggleFollow) return;

    setFollowLoading(true);
    try {
      await toggleFollow();
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  // Eliminar logs de depuración que podrían exponer el ID
  console.log('ProfileHeader rendering:', {
    isCurrentUser,
    currentUser: currentUser?.uid,
    photoURL: userData?.photoURL,
    displayName: userData?.displayName
  });

  // Si userData es null, mostrar un mensaje de carga
  if (!userData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-domired-600 mx-auto"></div>
        <p className="mt-4 text-gray-500 dark:text-gray-400">Cargando perfil...</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
      {/* Cover photo */}
      <div className="relative h-48 bg-gray-200 dark:bg-gray-700">
        {userData.coverPhotoURL ? (
          <img
            src={userData.coverPhotoURL}
            alt="Portada"
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = '';
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-domired-500 to-domired-300 opacity-80"></div>
        )}

        {/* Edit cover photo button */}
        {isCurrentUser && (
          <label
            htmlFor="cover-photo-upload"
            className="absolute top-4 right-4 bg-white dark:bg-gray-800 p-2 rounded-full shadow-md cursor-pointer"
            aria-label="Cambiar foto de portada"
          >
            <CameraIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <input
              id="cover-photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleCoverPhotoChange}
              disabled={isUploading}
            />
          </label>
        )}
      </div>

      {/* Profile Info */}
      <div className="relative px-4 sm:px-6 pb-4">
        {/* Profile Photo */}
        <div className="relative -mt-16 mb-4">
          <div className="w-32 h-32 mx-auto rounded-full border-4 border-white dark:border-gray-800 overflow-hidden bg-gray-200 dark:bg-gray-600">
            <img
              src={userData?.photoURL || '/default-avatar.png'}
              alt={userData?.displayName || 'Usuario'}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = '/default-avatar.png';
              }}
            />
          </div>

          {isCurrentUser && (
            <label className="absolute bottom-0 right-0 sm:right-1/3 bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
              <CameraIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePhotoChange}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          )}
        </div>

        {/* Upload Progress */}
        {isUploading && (
          <div className="mb-4 mx-auto max-w-md">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
              <div
                className="h-2 bg-blue-500 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-xs text-center mt-1 text-gray-500 dark:text-gray-400">
              Subiendo... {Math.round(uploadProgress)}%
            </p>
          </div>
        )}

        {/* User Info */}
        <div className="text-center">
          {isEditing ? (
            <div className="space-y-3 max-w-md mx-auto">
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Nombre
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
                  required
                />
              </div>

              <div>
                <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Biografía
                </label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
                  rows={3}
                />
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ubicación
                </label>
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
                />
              </div>

              <div className="flex space-x-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleProfileUpdate}
                  className="px-4 py-2 text-sm font-medium text-white bg-domired-600 rounded-md hover:bg-domired-700"
                >
                  Guardar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center">
                <h1 className="text-2xl font-bold">{userData?.displayName || 'Usuario'}</h1>
                {isCurrentUser && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="ml-2 p-1 text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              {/* Ocultar cualquier texto que pueda ser un ID */}

              {userData?.bio && (
                <p className="mt-2 text-gray-600 dark:text-gray-300">
                  {userData.bio}
                </p>
              )}

              {userData?.location && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {userData.location}
                </p>
              )}

              <div className="flex items-center justify-center space-x-6 mt-4">
                <Link
                  to={`/profile/${userData.uid}/followers`}
                  className="text-center text-gray-600 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400 transition-colors"
                >
                  <div className="flex flex-col items-center">
                    <span className="font-bold text-lg text-gray-900 dark:text-white">{userData?.followers || 0}</span>
                    <span className="flex items-center">
                      <UsersIcon className="h-4 w-4 mr-1" />
                      Seguidores
                    </span>
                  </div>
                </Link>
                <Link
                  to={`/profile/${userData.uid}/following`}
                  className="text-center text-gray-600 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400 transition-colors"
                >
                  <div className="flex flex-col items-center">
                    <span className="font-bold text-lg text-gray-900 dark:text-white">{userData?.following || 0}</span>
                    <span className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      Siguiendo
                    </span>
                  </div>
                </Link>
              </div>

              {/* Botones de acción */}
              {!isCurrentUser && currentUser && toggleFollow && (
                <div className="flex space-x-2">
                  {/* Botón de seguir/dejar de seguir */}
                  <button
                    onClick={handleToggleFollow}
                    disabled={followLoading}
                    className={`mt-4 px-6 py-2 rounded-full text-sm font-medium ${isFollowing
                      ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                      : 'bg-domired-600 text-white hover:bg-domired-700'
                      } transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50 flex items-center justify-center`}
                  >
                    {followLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Procesando...
                      </>
                    ) : isFollowing ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        Siguiendo
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                        </svg>
                        Seguir
                      </>
                    )}
                  </button>

                  {/* Botón de mensaje directo */}
                  <Link
                    to={`/messages?user=${userData.uid}`}
                    className="px-4 py-1.5 rounded-full text-sm font-medium bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 flex items-center justify-center"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                    Mensaje
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
